import { normalizePatientHeightCm, toFiniteNumber } from '../utils/patientMetrics';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import {
  PEAK_FLOW_POLGAR_1971_CHILD_HEIGHTS_CM,
  PEAK_FLOW_POLGAR_1971_CHILD_VALUES_L_MIN,
  PEAK_FLOW_ZHAO_2021_FEMALE_MSPLINE,
  PEAK_FLOW_ZHAO_2021_MALE_MSPLINE,
  PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MAX,
  PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MIN,
  PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_STEP,
} from './peakFlowReferenceData';

export type PeakFlowReferenceMode = 'none' | 'personal_best' | 'manual_reference' | 'predicted_reference';
export type PeakFlowZoneKey = 'green' | 'yellow' | 'red' | 'unclassified';
export type PeakFlowSeverityKey = 'urgent' | 'warning' | 'positive' | 'stable';
export type PeakFlowPredictedReferenceSourceKey =
  | 'ji_2021_charls'
  | 'zhao_2021_charls'
  | 'leiner_1963_adult_male'
  | 'leiner_1963_adult_female'
  | 'polgar_promadhat_1971_child';
export type PeakFlowPredictedReferenceSex = 'male' | 'female';

export type PeakFlowSymptomKey =
  | 'wheeze'
  | 'cough'
  | 'chestTightness'
  | 'shortnessOfBreath'
  | 'nocturnalSymptoms'
  | 'triggerExposure'
  | 'relieverUseToday';

export type PeakFlowDangerSignKey =
  | 'troubleWalkingOrTalking'
  | 'lipsBlue'
  | 'severeBreathlessness'
  | 'rapidWorsening';

export interface PeakFlowSymptomFlags {
  wheeze: boolean;
  cough: boolean;
  chestTightness: boolean;
  shortnessOfBreath: boolean;
  nocturnalSymptoms: boolean;
  triggerExposure: boolean;
  relieverUseToday: boolean;
}

export interface PeakFlowDangerSigns {
  troubleWalkingOrTalking: boolean;
  lipsBlue: boolean;
  severeBreathlessness: boolean;
  rapidWorsening: boolean;
}

export interface PeakFlowResultPayload {
  preReadings: number[];
  postReadings?: number[];
  bronchodilatorUsed?: boolean;
  bronchodilatorMedication?: string | null;
  bronchodilatorWaitMinutes?: number | null;
  referenceMode?: PeakFlowReferenceMode;
  referenceValue?: number | null;
  referenceLabel?: string | null;
  referenceSourceKey?: PeakFlowPredictedReferenceSourceKey | null;
  referenceSourceLabel?: string | null;
  referencePopulationNote?: string | null;
  symptoms?: Partial<PeakFlowSymptomFlags>;
  dangerSigns?: Partial<PeakFlowDangerSigns>;
  notes?: string | null;
  interpretation?: string | null;
  referenceNote?: string | null;
  finishedAt?: string;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
}

export interface PeakFlowSummary {
  bestPreFlow: number | null;
  bestPostFlow: number | null;
  preAttemptCount: number;
  postAttemptCount: number;
  bronchodilatorUsed: boolean;
  bronchodilatorMedication: string | null;
  bronchodilatorWaitMinutes: number | null;
  referenceMode: PeakFlowReferenceMode;
  referenceValue: number | null;
  referenceLabel: string;
  referenceSourceKey: PeakFlowPredictedReferenceSourceKey | null;
  referenceSourceLabel: string | null;
  referencePopulationNote: string | null;
  referencePercentPre: number | null;
  referencePercentPost: number | null;
  zone: PeakFlowZoneKey;
  zoneLabel: string;
  zoneDescription: string;
  symptomCount: number;
  dangerSignCount: number;
  symptomLabels: string[];
  dangerSignLabels: string[];
  bronchodilatorDeltaAbsolute: number | null;
  bronchodilatorDeltaPercent: number | null;
  bronchodilatorResponseThresholdPercent: number | null;
  bronchodilatorResponseSupported: boolean | null;
  severityKey: PeakFlowSeverityKey;
  severity: string;
  severityTone: string;
  scoreColor: string;
  actionLabel: string;
  interpretation: string;
  patientInterpretation: string;
  referenceNote: string;
}

export interface PeakFlowPredictedReferenceSourceDefinition {
  key: PeakFlowPredictedReferenceSourceKey;
  label: string;
  shortLabel: string;
  populationLabel: string;
  requirementsLabel: string;
  ageRangeLabel: string | null;
  heightRangeLabel: string;
  impliedSex: PeakFlowPredictedReferenceSex | null;
}

export interface PeakFlowPredictedReferenceResult {
  sourceKey: PeakFlowPredictedReferenceSourceKey;
  sourceLabel: string;
  shortLabel: string;
  populationLabel: string;
  requirementsLabel: string;
  ageRangeLabel: string | null;
  heightRangeLabel: string;
  ageYears: number | null;
  heightCm: number | null;
  sex: PeakFlowPredictedReferenceSex | null;
  predictedValue: number | null;
  unavailableReason: string | null;
}

type PeakFlowReferenceOption = {
  value: PeakFlowReferenceMode;
  label: string;
  description: string;
};

type PeakFlowFlagDefinition<T extends string> = {
  key: T;
  label: string;
  description: string;
};

type PeakFlowSeverityBand = {
  label: string;
  tone: string;
  scoreColor: string;
  actionLabel: string;
};

const DEFAULT_SYMPTOMS: PeakFlowSymptomFlags = {
  wheeze: false,
  cough: false,
  chestTightness: false,
  shortnessOfBreath: false,
  nocturnalSymptoms: false,
  triggerExposure: false,
  relieverUseToday: false,
};

const DEFAULT_DANGER_SIGNS: PeakFlowDangerSigns = {
  troubleWalkingOrTalking: false,
  lipsBlue: false,
  severeBreathlessness: false,
  rapidWorsening: false,
};

const ZONE_LABELS: Record<PeakFlowZoneKey, { label: string; description: string }> = {
  green: {
    label: 'Zona verde',
    description: '80-100% da referência registrada.',
  },
  yellow: {
    label: 'Zona amarela',
    description: '50-79% da referência registrada.',
  },
  red: {
    label: 'Zona vermelha',
    description: 'Abaixo de 50% da referência registrada.',
  },
  unclassified: {
    label: 'Sem zona',
    description: 'Sem referência suficiente para classificar em zonas.',
  },
};

const SEVERITY_BANDS: Record<PeakFlowSeverityKey, PeakFlowSeverityBand> = {
  urgent: {
    label: 'Ação urgente',
    tone: 'bg-rose-100 text-rose-700',
    scoreColor: '#e11d48',
    actionLabel: 'Revisar imediatamente e considerar encaminhamento de urgência conforme avaliação clínica.',
  },
  warning: {
    label: 'Faixa de alerta',
    tone: 'bg-amber-100 text-amber-700',
    scoreColor: '#d97706',
    actionLabel: 'Revisar no mesmo dia, correlacionar com sintomas e ajustar o plano conforme conduta profissional.',
  },
  positive: {
    label: 'Triagem positiva',
    tone: 'bg-sky-100 text-sky-700',
    scoreColor: '#0284c7',
    actionLabel: 'Registrar seguimento funcional e considerar confirmação com espirometria quando disponível.',
  },
  stable: {
    label: 'Sem alerta imediato',
    tone: 'bg-emerald-100 text-emerald-700',
    scoreColor: '#059669',
    actionLabel: 'Sem sinal objetivo de urgência neste registro; manter acompanhamento clínico e reavaliação se necessário.',
  },
};

export const PEAK_FLOW_REFERENCE_NOTE =
  'Usar sempre o mesmo medidor e registrar a melhor de 3 manobras em cada momento. A resposta ao broncodilatador pode apoiar suspeita de obstrução expiratória variável quando há correlação clínica. As zonas de ação são baseadas na referência registrada pelo profissional. O PEF isolado não confirma nem exclui asma e não substitui espirometria quando disponível.';

export const PEAK_FLOW_DEFAULT_PREDICTED_REFERENCE_SOURCE_KEY: PeakFlowPredictedReferenceSourceKey = 'ji_2021_charls';

const PEAK_FLOW_PREDICTED_REFERENCE_SOURCE_MAP: Record<
  PeakFlowPredictedReferenceSourceKey,
  PeakFlowPredictedReferenceSourceDefinition
> = {
  ji_2021_charls: {
    key: 'ji_2021_charls',
    label: 'Ji et al. 2021 (CHARLS)',
    shortLabel: 'valor previsto Ji 2021',
    populationLabel: `adultos chineses não fumantes de ${PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MIN} a ${PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MAX} anos`,
    requirementsLabel: 'sexo, idade e altura',
    ageRangeLabel: `${PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MIN} a ${PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MAX} anos`,
    heightRangeLabel: 'sem faixa fixa no artigo, desde que a altura esteja cadastrada',
    impliedSex: null,
  },
  zhao_2021_charls: {
    key: 'zhao_2021_charls',
    label: 'Ji et al. 2021 (CHARLS) — chave legada',
    shortLabel: 'valor previsto Ji 2021',
    populationLabel: `adultos chineses não fumantes de ${PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MIN} a ${PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MAX} anos`,
    requirementsLabel: 'sexo, idade e altura',
    ageRangeLabel: `${PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MIN} a ${PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MAX} anos`,
    heightRangeLabel: 'sem faixa fixa no artigo, desde que a altura esteja cadastrada',
    impliedSex: null,
  },
  leiner_1963_adult_male: {
    key: 'leiner_1963_adult_male',
    label: 'Leiner et al. 1963 - homens adultos',
    shortLabel: 'valor previsto Leiner homens',
    populationLabel: 'homens adultos; tabela por idade e estatura',
    requirementsLabel: 'idade e estatura',
    ageRangeLabel: '20 a 70 anos (faixa tabulada)',
    heightRangeLabel: '155 a 180 cm (faixa tabulada)',
    impliedSex: 'male',
  },
  leiner_1963_adult_female: {
    key: 'leiner_1963_adult_female',
    label: 'Leiner et al. 1963 - mulheres adultas',
    shortLabel: 'valor previsto Leiner mulheres',
    populationLabel: 'mulheres adultas; tabela por idade e estatura',
    requirementsLabel: 'idade e estatura',
    ageRangeLabel: '20 a 70 anos (faixa tabulada)',
    heightRangeLabel: '145 a 170 cm (faixa tabulada)',
    impliedSex: 'female',
  },
  polgar_promadhat_1971_child: {
    key: 'polgar_promadhat_1971_child',
    label: 'Polgar e Promadhat 1971 - crianças e adolescentes',
    shortLabel: 'valor previsto Polgar 1971',
    populationLabel: 'crianças e adolescentes; tabela por estatura',
    requirementsLabel: 'estatura',
    ageRangeLabel: null,
    heightRangeLabel: '110 a 170 cm',
    impliedSex: null,
  },
};

export const PEAK_FLOW_PREDICTED_REFERENCE_SOURCES: PeakFlowPredictedReferenceSourceDefinition[] = [
  PEAK_FLOW_PREDICTED_REFERENCE_SOURCE_MAP.leiner_1963_adult_male,
  PEAK_FLOW_PREDICTED_REFERENCE_SOURCE_MAP.leiner_1963_adult_female,
  PEAK_FLOW_PREDICTED_REFERENCE_SOURCE_MAP.polgar_promadhat_1971_child,
  PEAK_FLOW_PREDICTED_REFERENCE_SOURCE_MAP.ji_2021_charls,
];

export const PEAK_FLOW_PREDICTED_REFERENCE_NOTE =
  'Quando o valor previsto automático for usado, interpretar conforme a base selecionada pelo profissional. Essa referência é útil para triagem inicial, mas não substitui o melhor pessoal do paciente.';

export const PEAK_FLOW_REFERENCE_OPTIONS: PeakFlowReferenceOption[] = [
  {
    value: 'none',
    label: 'Sem referência',
    description: 'Registrar apenas o valor medido e a triagem clínica do momento.',
  },
  {
    value: 'personal_best',
    label: 'Melhor pessoal',
    description: 'Usar o melhor valor conhecido do paciente para definir zonas de ação.',
  },
  {
    value: 'manual_reference',
    label: 'Valor de referência',
    description: 'Permite comparar com uma referência definida pela clínica no atendimento inicial.',
  },
  {
    value: 'predicted_reference',
    label: 'Valor previsto automático',
    description: 'Calcula o previsto pela base selecionada quando os dados necessários estiverem disponíveis.',
  },
];

export const PEAK_FLOW_SYMPTOM_DEFINITIONS: PeakFlowFlagDefinition<PeakFlowSymptomKey>[] = [
  { key: 'wheeze', label: 'Chiado ou sibilância', description: 'Paciente refere ou apresenta sibilância.' },
  { key: 'cough', label: 'Tosse', description: 'Tosse atual associada ao quadro respiratório.' },
  { key: 'chestTightness', label: 'Aperto no peito', description: 'Sensação de opressão torácica.' },
  { key: 'shortnessOfBreath', label: 'Falta de ar', description: 'Dispneia no repouso ou aos esforços.' },
  { key: 'nocturnalSymptoms', label: 'Sintomas noturnos', description: 'Despertar ou piora noturna.' },
  { key: 'triggerExposure', label: 'Gatilho recente', description: 'Exposição recente a frio, exercício, irritantes ou alérgenos.' },
  { key: 'relieverUseToday', label: 'Resgate hoje', description: 'Necessidade de medicação de alívio no dia da avaliação.' },
];

export const PEAK_FLOW_DANGER_SIGN_DEFINITIONS: PeakFlowFlagDefinition<PeakFlowDangerSignKey>[] = [
  { key: 'troubleWalkingOrTalking', label: 'Dificuldade para andar ou falar', description: 'Sinal clínico de gravidade respiratória.' },
  { key: 'lipsBlue', label: 'Lábios ou unhas arroxeados', description: 'Possível hipoxemia clinicamente relevante.' },
  { key: 'severeBreathlessness', label: 'Dispneia importante em repouso', description: 'Desconforto respiratório intenso no momento da medida.' },
  { key: 'rapidWorsening', label: 'Piora rápida recente', description: 'Relato de deterioração acelerada do quadro.' },
];

const PEAK_FLOW_ZHAO_2021_EQUATIONS = {
  male: {
    intercept: 100.4273,
    ageCoefficient: -4.94,
    heightCoefficient: 3.4654,
    splineValues: PEAK_FLOW_ZHAO_2021_MALE_MSPLINE,
  },
  female: {
    intercept: 66.9815,
    ageCoefficient: -2.7342,
    heightCoefficient: 2.3084,
    splineValues: PEAK_FLOW_ZHAO_2021_FEMALE_MSPLINE,
  },
} as const;

const PEAK_FLOW_LEINER_1963_EQUATIONS = {
  male: {
    baseHeightCoefficient: 3.95,
    ageHeightCoefficient: 0.0151,
  },
  female: {
    baseHeightCoefficient: 2.93,
    ageHeightCoefficient: 0.0072,
  },
} as const;

export const normalizePeakFlowValue = (value: unknown): number | null => {
  const normalized =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseFloat(value.replace(',', '.'))
        : Number.parseFloat(String(value ?? ''));

  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
};

const readNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const normalizeBooleanFlags = <T extends string>(
  source: unknown,
  defaults: Record<T, boolean>,
): Record<T, boolean> => {
  const next = { ...defaults };
  if (!source || typeof source !== 'object') return next;

  for (const key of Object.keys(defaults) as T[]) {
    next[key] = Boolean((source as Record<string, unknown>)[key]);
  }
  return next;
};

const normalizeReadings = (value: unknown): number[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map(normalizePeakFlowValue)
    .filter((item): item is number => item != null)
    .slice(0, 3);
};

const resolveReferenceMode = (value: unknown): PeakFlowReferenceMode => {
  if (value === 'personal_best' || value === 'manual_reference' || value === 'predicted_reference' || value === 'none') {
    return value;
  }
  return 'none';
};

export const resolvePeakFlowPredictedReferenceSourceKey = (
  value: unknown,
): PeakFlowPredictedReferenceSourceKey | null => {
  if (
    value === 'ji_2021_charls' ||
    value === 'zhao_2021_charls' ||
    value === 'leiner_1963_adult_male' ||
    value === 'leiner_1963_adult_female' ||
    value === 'polgar_promadhat_1971_child'
  ) {
    return value;
  }
  return null;
};

export const getPeakFlowPredictedReferenceSourceDefinition = (
  sourceKey: PeakFlowPredictedReferenceSourceKey,
): PeakFlowPredictedReferenceSourceDefinition => PEAK_FLOW_PREDICTED_REFERENCE_SOURCE_MAP[sourceKey];

const resolveReferenceLabel = (
  mode: PeakFlowReferenceMode,
  value?: string | null,
  referenceSourceKey?: PeakFlowPredictedReferenceSourceKey | null,
): string => {
  if (value && value.trim().length > 0) return value.trim();
  if (mode === 'personal_best') return 'melhor pessoal';
  if (mode === 'manual_reference') return 'valor de referência';
  if (mode === 'predicted_reference') {
    return referenceSourceKey ? getPeakFlowPredictedReferenceSourceDefinition(referenceSourceKey).shortLabel : 'valor previsto';
  }
  return 'referência';
};

const resolveBestReading = (readings: number[]): number | null => (readings.length ? Math.max(...readings) : null);

const calculatePercent = (value: number | null, reference: number | null): number | null => {
  if (value == null || reference == null || reference <= 0) return null;
  return Number(((value / reference) * 100).toFixed(1));
};

const resolveZone = (percent: number | null): PeakFlowZoneKey => {
  if (percent == null) return 'unclassified';
  if (percent < 50) return 'red';
  if (percent < 80) return 'yellow';
  return 'green';
};

const formatNumber = (value: number | null, decimals = 0): string => {
  if (value == null || Number.isNaN(value)) return '—';
  const fixed = Number(value.toFixed(decimals));
  return fixed.toString().replace('.', ',');
};

export const formatPeakFlowValue = (value: number | null): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return Number.isInteger(value) ? String(value) : formatNumber(value, 1);
};

export const formatPeakFlowPercent = (value: number | null): string =>
  value == null || Number.isNaN(value) ? '—' : `${formatNumber(value, 1)}%`;

const normalizeReferenceSexToken = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();

export const resolvePeakFlowPredictedReferenceSex = (value: unknown): PeakFlowPredictedReferenceSex | null => {
  if (typeof value !== 'string' || value.trim().length === 0) return null;
  const normalized = normalizeReferenceSexToken(value);

  if (
    normalized === 'F' ||
    normalized.startsWith('FEM') ||
    normalized.startsWith('FEMALE') ||
    normalized.startsWith('MULHER') ||
    normalized.startsWith('WOMAN')
  ) {
    return 'female';
  }

  if (
    normalized === 'M' ||
    normalized.startsWith('MASC') ||
    normalized.startsWith('MALE') ||
    normalized.startsWith('HOMEM') ||
    normalized.startsWith('MAN')
  ) {
    return 'male';
  }

  return null;
};

const getAxisBounds = (axis: number[], value: number): { lowerIndex: number; upperIndex: number } | null => {
  if (value < axis[0] || value > axis[axis.length - 1]) return null;

  const upperIndex = axis.findIndex((entry) => entry >= value);
  if (upperIndex === -1) {
    const lastIndex = axis.length - 1;
    return { lowerIndex: lastIndex, upperIndex: lastIndex };
  }

  if (axis[upperIndex] === value || upperIndex === 0) {
    return { lowerIndex: upperIndex, upperIndex };
  }

  return { lowerIndex: upperIndex - 1, upperIndex };
};

const interpolateLinear = (axis: number[], values: number[], target: number): number | null => {
  const bounds = getAxisBounds(axis, target);
  if (!bounds) return null;

  const { lowerIndex, upperIndex } = bounds;
  const lowerValue = values[lowerIndex];
  const upperValue = values[upperIndex];

  if (!Number.isFinite(lowerValue)) return null;
  if (lowerIndex === upperIndex || !Number.isFinite(upperValue)) return lowerValue;

  const lowerAxis = axis[lowerIndex];
  const upperAxis = axis[upperIndex];
  const fraction = (target - lowerAxis) / (upperAxis - lowerAxis);

  return lowerValue + (upperValue - lowerValue) * fraction;
};

const interpolateSplineValue = (values: number[], ageYears: number): number | null => {
  if (ageYears < PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MIN || ageYears > PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MAX) return null;

  const scaledIndex = (ageYears - PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MIN) / PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_STEP;
  const lowerIndex = Math.floor(scaledIndex);
  const upperIndex = Math.ceil(scaledIndex);
  const lowerValue = values[lowerIndex];
  const upperValue = values[upperIndex];

  if (!Number.isFinite(lowerValue)) return null;
  if (lowerIndex === upperIndex || !Number.isFinite(upperValue)) return lowerValue;

  const fraction = scaledIndex - lowerIndex;
  return lowerValue + (upperValue - lowerValue) * fraction;
};

const calculatePredictedPeakFlowValueFromJi = (
  sex: PeakFlowPredictedReferenceSex,
  ageYears: number,
  heightCm: number,
): number | null => {
  const equation = PEAK_FLOW_ZHAO_2021_EQUATIONS[sex];
  const mspline = interpolateSplineValue(equation.splineValues, ageYears);
  if (mspline == null) return null;

  const predicted =
    equation.intercept +
    equation.ageCoefficient * ageYears +
    equation.heightCoefficient * heightCm +
    mspline;

  return Number(predicted.toFixed(1));
};

const calculatePredictedPeakFlowValueFromLeiner = (
  sex: PeakFlowPredictedReferenceSex,
  ageYears: number,
  heightCm: number,
): number => {
  const equation = PEAK_FLOW_LEINER_1963_EQUATIONS[sex];
  return Number(((equation.baseHeightCoefficient - equation.ageHeightCoefficient * ageYears) * heightCm).toFixed(1));
};

export const resolveSuggestedPeakFlowPredictedReferenceSourceKey = (
  patientContext?: Record<string, unknown> | null,
): PeakFlowPredictedReferenceSourceKey => {
  const ageYears = toFiniteNumber(patientContext?.ageYears);
  const sex = resolvePeakFlowPredictedReferenceSex(patientContext?.sex);

  if (ageYears != null && ageYears < 20) return 'polgar_promadhat_1971_child';

  if (ageYears != null && ageYears >= PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MIN && ageYears <= PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MAX) {
    return 'ji_2021_charls';
  }

  if (sex === 'female' && ageYears != null && ageYears >= 20 && ageYears <= 70) return 'leiner_1963_adult_female';
  if (sex === 'male' && ageYears != null && ageYears >= 20 && ageYears <= 70) return 'leiner_1963_adult_male';

  if (sex === 'female') return 'leiner_1963_adult_female';
  if (sex === 'male') return 'leiner_1963_adult_male';
  return PEAK_FLOW_DEFAULT_PREDICTED_REFERENCE_SOURCE_KEY;
};

export const calculatePredictedPeakFlowReference = (
  patientContext?: Record<string, unknown> | null,
  sourceKey: PeakFlowPredictedReferenceSourceKey = resolveSuggestedPeakFlowPredictedReferenceSourceKey(patientContext),
): PeakFlowPredictedReferenceResult => {
  const sourceDefinition = getPeakFlowPredictedReferenceSourceDefinition(sourceKey);
  const ageYears = toFiniteNumber(patientContext?.ageYears);
  const heightCm = normalizePatientHeightCm(patientContext?.heightCm);
  const patientSex = resolvePeakFlowPredictedReferenceSex(patientContext?.sex);

  let sex: PeakFlowPredictedReferenceSex | null = sourceDefinition.impliedSex ?? patientSex;
  let unavailableReason: string | null = null;
  let predictedValue: number | null = null;

  switch (sourceKey) {
    case 'ji_2021_charls':
    case 'zhao_2021_charls':
      if (patientSex == null) {
        unavailableReason = 'Sexo do paciente indisponível para calcular o valor previsto nesta referência.';
      } else if (ageYears == null) {
        unavailableReason = 'Idade do paciente indisponível para calcular o valor previsto nesta referência.';
      } else if (heightCm == null) {
        unavailableReason = 'Altura do paciente indisponível para calcular o valor previsto nesta referência.';
      } else if (ageYears < PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MIN || ageYears > PEAK_FLOW_ZHAO_2021_REFERENCE_AGE_MAX) {
        unavailableReason = `A referência ${sourceDefinition.label} cobre apenas ${sourceDefinition.ageRangeLabel}.`;
      } else {
        predictedValue = calculatePredictedPeakFlowValueFromJi(patientSex, ageYears, heightCm);
        sex = patientSex;
        if (predictedValue == null) {
          unavailableReason = 'Não foi possível interpolar a tabela suplementar da referência selecionada.';
        }
      }
      break;
    case 'leiner_1963_adult_male':
      if (ageYears == null) {
        unavailableReason = 'Idade do paciente indisponível para calcular o valor previsto nesta referência.';
      } else if (heightCm == null) {
        unavailableReason = 'Altura do paciente indisponível para calcular o valor previsto nesta referência.';
      } else {
        predictedValue = calculatePredictedPeakFlowValueFromLeiner('male', ageYears, heightCm);
      }
      sex = 'male';
      break;
    case 'leiner_1963_adult_female':
      if (ageYears == null) {
        unavailableReason = 'Idade do paciente indisponível para calcular o valor previsto nesta referência.';
      } else if (heightCm == null) {
        unavailableReason = 'Altura do paciente indisponível para calcular o valor previsto nesta referência.';
      } else {
        predictedValue = calculatePredictedPeakFlowValueFromLeiner('female', ageYears, heightCm);
      }
      sex = 'female';
      break;
    case 'polgar_promadhat_1971_child':
      if (heightCm == null) {
        unavailableReason = 'Altura do paciente indisponível para calcular o valor previsto nesta referência.';
      } else if (
        heightCm < PEAK_FLOW_POLGAR_1971_CHILD_HEIGHTS_CM[0] ||
        heightCm > PEAK_FLOW_POLGAR_1971_CHILD_HEIGHTS_CM[PEAK_FLOW_POLGAR_1971_CHILD_HEIGHTS_CM.length - 1]
      ) {
        unavailableReason = `A referência ${sourceDefinition.label} cobre estaturas de ${sourceDefinition.heightRangeLabel}.`;
      } else {
        predictedValue = interpolateLinear(
          PEAK_FLOW_POLGAR_1971_CHILD_HEIGHTS_CM,
          PEAK_FLOW_POLGAR_1971_CHILD_VALUES_L_MIN,
          heightCm,
        );
        if (predictedValue == null) {
          unavailableReason = 'Não foi possível interpolar a tabela da referência selecionada.';
        }
      }
      sex = null;
      break;
  }

  return {
    sourceKey,
    sourceLabel: sourceDefinition.label,
    shortLabel: sourceDefinition.shortLabel,
    populationLabel: sourceDefinition.populationLabel,
    requirementsLabel: sourceDefinition.requirementsLabel,
    ageRangeLabel: sourceDefinition.ageRangeLabel,
    heightRangeLabel: sourceDefinition.heightRangeLabel,
    ageYears,
    heightCm,
    sex,
    predictedValue: predictedValue != null ? Number(predictedValue.toFixed(1)) : null,
    unavailableReason,
  };
};

const buildPeakFlowReferenceNote = (
  referenceMode: PeakFlowReferenceMode,
  referenceSourceLabel?: string | null,
  referencePopulationNote?: string | null,
): string => {
  if (referenceMode !== 'predicted_reference') return PEAK_FLOW_REFERENCE_NOTE;

  const sourceFragment = referenceSourceLabel ? ` A referência automática desta avaliação foi calculada com ${referenceSourceLabel}.` : '';
  const populationFragment = referencePopulationNote ? ` Base populacional/tabela: ${referencePopulationNote}.` : '';

  return `${PEAK_FLOW_REFERENCE_NOTE} ${PEAK_FLOW_PREDICTED_REFERENCE_NOTE}${sourceFragment}${populationFragment}`.trim();
};

export const normalizePeakFlowPayload = (value: unknown): PeakFlowResultPayload | null => {
  if (!value || typeof value !== 'object') return null;

  const source = value as Record<string, unknown>;
  const preReadings = normalizeReadings(source.preReadings);
  const postReadings = normalizeReadings(source.postReadings);
  const referenceMode = resolveReferenceMode(source.referenceMode);
  const patientContext =
    source.patientContext && typeof source.patientContext === 'object'
      ? (source.patientContext as Record<string, unknown>)
      : undefined;
  const resolvedReferenceSourceKey =
    referenceMode === 'predicted_reference'
      ? resolvePeakFlowPredictedReferenceSourceKey(source.referenceSourceKey) ?? PEAK_FLOW_DEFAULT_PREDICTED_REFERENCE_SOURCE_KEY
      : null;
  const predictedReference =
    referenceMode === 'predicted_reference'
      ? calculatePredictedPeakFlowReference(patientContext, resolvedReferenceSourceKey)
      : null;
  let referenceValue = normalizePeakFlowValue(source.referenceValue);

  if (referenceMode === 'predicted_reference' && referenceValue == null) {
    referenceValue = predictedReference?.predictedValue ?? null;
  }

  const bronchodilatorUsed = Boolean(source.bronchodilatorUsed) || postReadings.length > 0;
  const symptoms = normalizeBooleanFlags(source.symptoms, DEFAULT_SYMPTOMS);
  const dangerSigns = normalizeBooleanFlags(source.dangerSigns, DEFAULT_DANGER_SIGNS);
  const notes = readNonEmptyString(source.notes);
  const interpretation = readNonEmptyString(source.interpretation);
  const referenceSourceLabel =
    readNonEmptyString(source.referenceSourceLabel) ??
    (resolvedReferenceSourceKey ? getPeakFlowPredictedReferenceSourceDefinition(resolvedReferenceSourceKey).label : null);
  const referencePopulationNote =
    readNonEmptyString(source.referencePopulationNote) ??
    (resolvedReferenceSourceKey ? getPeakFlowPredictedReferenceSourceDefinition(resolvedReferenceSourceKey).populationLabel : null);
  const referenceNote =
    readNonEmptyString(source.referenceNote) ??
    buildPeakFlowReferenceNote(referenceMode, referenceSourceLabel, referencePopulationNote);
  const finishedAt = readNonEmptyString(source.finishedAt) ?? undefined;
  const hasFlags = [...Object.values(symptoms), ...Object.values(dangerSigns)].some(Boolean);
  const hasData =
    preReadings.length > 0 ||
    postReadings.length > 0 ||
    bronchodilatorUsed ||
    referenceMode !== 'none' ||
    referenceValue != null ||
    notes != null ||
    interpretation != null ||
    hasFlags;

  if (!hasData) return null;

  return {
    preReadings,
    postReadings: bronchodilatorUsed ? postReadings : [],
    bronchodilatorUsed,
    bronchodilatorMedication: readNonEmptyString(source.bronchodilatorMedication),
    bronchodilatorWaitMinutes: normalizePeakFlowValue(source.bronchodilatorWaitMinutes),
    referenceMode,
    referenceValue,
    referenceLabel: resolveReferenceLabel(
      referenceMode,
      readNonEmptyString(source.referenceLabel),
      resolvedReferenceSourceKey,
    ),
    referenceSourceKey: resolvedReferenceSourceKey,
    referenceSourceLabel,
    referencePopulationNote,
    symptoms,
    dangerSigns,
    notes,
    interpretation,
    referenceNote,
    finishedAt,
    patientContext,
    professionalContext:
      source.professionalContext && typeof source.professionalContext === 'object'
        ? (source.professionalContext as Record<string, unknown>)
        : undefined,
  };
};

const resolveSymptomLabels = (flags: PeakFlowSymptomFlags): string[] =>
  PEAK_FLOW_SYMPTOM_DEFINITIONS.filter((item) => flags[item.key]).map((item) => item.label);

const resolveDangerSignLabels = (flags: PeakFlowDangerSigns): string[] =>
  PEAK_FLOW_DANGER_SIGN_DEFINITIONS.filter((item) => flags[item.key]).map((item) => item.label);

const resolveBronchodilatorThreshold = (patientContext?: Record<string, unknown>): number | null => {
  if (!patientContext || typeof patientContext !== 'object') return 20;
  const ageYears = normalizePeakFlowValue(patientContext.ageYears);
  if (ageYears != null && ageYears < 18) return 15;
  return 20;
};

const buildInterpretation = (input: {
  summary: Omit<PeakFlowSummary, 'interpretation' | 'patientInterpretation'>;
  payload: PeakFlowResultPayload | null;
}): string => {
  const { summary, payload } = input;
  const parts: string[] = [];

  if (summary.bestPreFlow != null) {
    const referenceSegment =
      summary.referencePercentPre != null
        ? `, equivalente a ${formatPeakFlowPercent(summary.referencePercentPre)} do ${summary.referenceLabel}`
        : '';
    const zoneSegment = summary.zone !== 'unclassified' ? ` (${summary.zoneLabel.toLowerCase()})` : '';
    parts.push(
      `Melhor pico expiratório pré-broncodilatador de ${formatPeakFlowValue(summary.bestPreFlow)} L/min${referenceSegment}${zoneSegment}.`,
    );
  } else {
    parts.push('Sem 3 manobras pré-broncodilatador válidas registradas.');
  }

  if (summary.referenceMode === 'predicted_reference' && summary.referenceSourceLabel) {
    const populationSegment = summary.referencePopulationNote ? ` para ${summary.referencePopulationNote}` : '';
    parts.push(`O valor previsto automático desta triagem foi calculado com a referência ${summary.referenceSourceLabel}${populationSegment}.`);
  }

  if (summary.bronchodilatorUsed) {
    if (summary.bestPostFlow != null && summary.bronchodilatorDeltaPercent != null && summary.bronchodilatorDeltaAbsolute != null) {
      const responseSentence = summary.bronchodilatorResponseSupported
        ? `A variação percentual atinge o limiar operacional de ${formatNumber(summary.bronchodilatorResponseThresholdPercent, 0)}% para apoio à obstrução expiratória variável.`
        : `A variação percentual não atinge o limiar operacional de ${formatNumber(summary.bronchodilatorResponseThresholdPercent, 0)}%.`;
      parts.push(
        `A melhor medida após broncodilatador foi ${formatPeakFlowValue(summary.bestPostFlow)} L/min, com ganho de ${formatPeakFlowValue(
          summary.bronchodilatorDeltaAbsolute,
        )} L/min e ${formatPeakFlowPercent(summary.bronchodilatorDeltaPercent)} sobre o pré. ${responseSentence}`,
      );
    } else {
      parts.push('A etapa pós-broncodilatador foi sinalizada, mas não há 3 medidas válidas para comparação objetiva.');
    }
  }

  if (summary.symptomLabels.length) {
    parts.push(`Sintomas marcados no momento: ${summary.symptomLabels.join(', ')}.`);
  }

  if (summary.dangerSignLabels.length) {
    parts.push(`Sinais de alarme registrados: ${summary.dangerSignLabels.join(', ')}.`);
  }

  if (summary.zone === 'unclassified' && summary.referenceMode === 'none') {
    parts.push('Sem melhor pessoal ou referência manual registrada, a classificação em zonas não pode ser aplicada neste atendimento.');
  }

  parts.push(`${summary.actionLabel} O PEF isolado não confirma nem exclui asma e deve ser correlacionado com história clínica e, quando possível, com espirometria.`);

  if (payload?.notes) {
    parts.push(`Observação do teste: ${payload.notes}.`);
  }

  return parts.join(' ');
};

const buildPatientInterpretation = (
  summary: Omit<PeakFlowSummary, 'interpretation' | 'patientInterpretation'>,
): string => {
  const parts: string[] = [];

  if (summary.bestPreFlow != null) {
    parts.push(`Pico de fluxo expiratório de ${formatPeakFlowValue(summary.bestPreFlow)} L/min.`);
  }

  if (summary.referencePercentPre != null) {
    parts.push(
      `O resultado corresponde a ${formatPeakFlowPercent(summary.referencePercentPre)} da referência e está na ${summary.zoneLabel.toLowerCase()}.`,
    );
  } else {
    parts.push('Não foi registrada uma referência individual para classificação em zonas.');
  }

  if (
    summary.bronchodilatorUsed &&
    summary.bestPostFlow != null &&
    summary.bronchodilatorDeltaAbsolute != null &&
    summary.bronchodilatorDeltaPercent != null
  ) {
    parts.push(
      `Após broncodilatador, o melhor valor foi ${formatPeakFlowValue(summary.bestPostFlow)} L/min, com variação de ${formatPeakFlowValue(
        summary.bronchodilatorDeltaAbsolute,
      )} L/min (${formatPeakFlowPercent(summary.bronchodilatorDeltaPercent)}).`,
    );
  }

  if (summary.dangerSignCount > 0) {
    parts.push('Foram registrados sinais de alarme que exigem avaliação clínica prioritária.');
  } else {
    parts.push(summary.actionLabel);
  }

  return parts.join(' ');
};

export const summarizePeakFlowPayload = (value: unknown): PeakFlowSummary => {
  const payload = normalizePeakFlowPayload(value);
  const preReadings = payload?.preReadings ?? [];
  const postReadings = payload?.postReadings ?? [];
  const bestPreFlow = resolveBestReading(preReadings);
  const bestPostFlow = resolveBestReading(postReadings);
  const symptoms = normalizeBooleanFlags(payload?.symptoms, DEFAULT_SYMPTOMS);
  const dangerSigns = normalizeBooleanFlags(payload?.dangerSigns, DEFAULT_DANGER_SIGNS);
  const symptomLabels = resolveSymptomLabels(symptoms);
  const dangerSignLabels = resolveDangerSignLabels(dangerSigns);
  const referenceMode = payload?.referenceMode ?? 'none';
  const referenceValue = payload?.referenceValue ?? null;
  const referenceSourceKey =
    payload?.referenceSourceKey ??
    (referenceMode === 'predicted_reference' ? PEAK_FLOW_DEFAULT_PREDICTED_REFERENCE_SOURCE_KEY : null);
  const referenceLabel = resolveReferenceLabel(referenceMode, payload?.referenceLabel ?? null, referenceSourceKey);
  const referenceSourceLabel =
    payload?.referenceSourceLabel ??
    (referenceSourceKey ? getPeakFlowPredictedReferenceSourceDefinition(referenceSourceKey).label : null);
  const referencePopulationNote =
    payload?.referencePopulationNote ??
    (referenceSourceKey ? getPeakFlowPredictedReferenceSourceDefinition(referenceSourceKey).populationLabel : null);
  const referencePercentPre = calculatePercent(bestPreFlow, referenceValue);
  const referencePercentPost = calculatePercent(bestPostFlow, referenceValue);
  const zone = referenceMode === 'personal_best'
    ? resolveZone(referencePercentPre)
    : 'unclassified';
  const zoneConfig = ZONE_LABELS[zone];
  const bronchodilatorDeltaAbsolute =
    bestPreFlow != null && bestPostFlow != null ? Number((bestPostFlow - bestPreFlow).toFixed(1)) : null;
  const bronchodilatorDeltaPercent =
    bronchodilatorDeltaAbsolute != null && bestPreFlow != null && bestPreFlow > 0
      ? Number(((bronchodilatorDeltaAbsolute / bestPreFlow) * 100).toFixed(1))
      : null;
  const bronchodilatorResponseThresholdPercent = resolveBronchodilatorThreshold(payload?.patientContext);
  const bronchodilatorResponseSupported =
    bronchodilatorDeltaPercent != null && bronchodilatorResponseThresholdPercent != null
      ? bronchodilatorDeltaPercent >= bronchodilatorResponseThresholdPercent
      : null;
  const symptomCount = symptomLabels.length;
  const dangerSignCount = dangerSignLabels.length;

  let severityKey: PeakFlowSeverityKey = 'stable';
  if (dangerSignCount > 0 || zone === 'red') {
    severityKey = 'urgent';
  } else if (zone === 'yellow') {
    severityKey = 'warning';
  } else if ((bronchodilatorResponseSupported && symptomCount > 0) || symptomCount >= 2 || bronchodilatorResponseSupported) {
    severityKey = 'positive';
  }

  const severityBand = SEVERITY_BANDS[severityKey];
  const referenceNote =
    payload?.referenceNote ?? buildPeakFlowReferenceNote(referenceMode, referenceSourceLabel, referencePopulationNote);
  const summaryBase = {
    bestPreFlow,
    bestPostFlow,
    preAttemptCount: preReadings.length,
    postAttemptCount: postReadings.length,
    bronchodilatorUsed: Boolean(payload?.bronchodilatorUsed),
    bronchodilatorMedication: payload?.bronchodilatorMedication ?? null,
    bronchodilatorWaitMinutes: payload?.bronchodilatorWaitMinutes ?? null,
    referenceMode,
    referenceValue,
    referenceLabel,
    referenceSourceKey,
    referenceSourceLabel,
    referencePopulationNote,
    referencePercentPre,
    referencePercentPost,
    zone,
    zoneLabel: zoneConfig.label,
    zoneDescription: zoneConfig.description,
    symptomCount,
    dangerSignCount,
    symptomLabels,
    dangerSignLabels,
    bronchodilatorDeltaAbsolute,
    bronchodilatorDeltaPercent,
    bronchodilatorResponseThresholdPercent,
    bronchodilatorResponseSupported,
    severityKey,
    severity: severityBand.label,
    severityTone: severityBand.tone,
    scoreColor: severityBand.scoreColor,
    actionLabel: severityBand.actionLabel,
    referenceNote,
  };

  const interpretation = resolveAssessmentInterpretationText(
    payload,
    buildInterpretation({ summary: summaryBase, payload }),
  );
  const patientInterpretation = buildPatientInterpretation(summaryBase);

  return {
    ...summaryBase,
    interpretation,
    patientInterpretation,
  };
};

export const getPeakFlowSeverityTone = (value: unknown): string => summarizePeakFlowPayload(value).severityTone;

export const getPeakFlowSeverityLabel = (value: unknown): string => summarizePeakFlowPayload(value).severity;

export const getPeakFlowScoreColor = (value: unknown): string => summarizePeakFlowPayload(value).scoreColor;

export const getPeakFlowZoneTone = (value: unknown): string => {
  const zone = summarizePeakFlowPayload(value).zone;
  if (zone === 'green') return 'bg-emerald-100 text-emerald-700';
  if (zone === 'yellow') return 'bg-amber-100 text-amber-700';
  if (zone === 'red') return 'bg-rose-100 text-rose-700';
  return 'bg-slate-100 text-slate-700';
};
